import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { color, breakpoint, space, shadow } from '@core-ds/primitives';
import constants from '@ifixit/toolbox/dist/constants/index';
import { withBreakpoints, BreakpointsContext } from 'core-components';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';
import {
   Logo,
   MenuIcon,
   Headings,
   SearchBar,
   MobileSearch,
   MobileMenu,
   Dropdowns,
} from './components';
import * as HeaderActions from './actions';
import { useModalIsOpen } from './shared-state';
import WorkbenchManager from 'Shared/workbench_manager';

const { animation } = constants;
const Section = styled.div`
   display: flex;
   align-items: center;
   transition: all 0.25s;
   animation: ${animation.fadeIn} 0.15s;

   ${props =>
      props.right &&
      css`
         flex: 1;
         justify-content: flex-end;
      `}
`;

const HeaderWrapper = styled.div`
   position: relative;
   background: ${color.black};
   box-shadow: ${shadow[2]};
   z-index: 100;
`;

const HeaderContainer = withBreakpoints(styled.div.attrs(props => ({
   className: 'mainHeader',
}))`
   position: relative;
   display: flex;
   background: ${color.black};
   height: 68px;
   max-width: 1400px;
   margin: 0 auto;
   padding: 0 ${space[3]};

   @media (min-width: 1440px) {
      padding: 0;
   }

   ${props =>
      props.breakpoints.isMobile() &&
      props.inMobileSearch &&
      css`
         ${Section} {
            opacity: 0;
            transform: translateY(100%);
            pointer-events: none;
            visibility: hidden;
         }
      `}
`);

const LogoContainer = styled.a`
   margin-left: ${space[4]};

   @media (min-width: 1440px) {
      margin-left: 0;
   }
`;

const LogoComponent = () => (
   <LogoContainer href="/" aria-label="home page">
      <Logo />
   </LogoContainer>
);

const mapStateToProps = state => ({
   headerState: state.headerState,
});

const mapDispatchToProps = {
   updateMiniCart: HeaderActions.updateMiniCart,
   toggleMobileMenu: HeaderActions.toggleMobileMenu,
};

const Header = props => {
   const {
      headerState,
      isOnCart,
      links,
      stores,
      dropdowns,
      toggleMobileMenu,
      updateMiniCart,
   } = props;

   useEffect(() => {
      const { miniCart } = dropdowns;

      // Add the HTML for MiniCart to Redux on mount.
      if (miniCart) {
         updateMiniCart(miniCart);
      }
   }, []);

   const breakpoints = React.useContext(BreakpointsContext);

   const [modalIsOpen] = useModalIsOpen();

   const mobileMenuClicked = useCallback(() => {
      toggleMobileMenu();
      document.getElementById('menuIcon').focus();
   }, [toggleMobileMenu]);

   if (breakpoints.isMobileTablet()) {
      return (
         <FocusLock disabled={!headerState.displayMobileMenu || modalIsOpen}>
            <RemoveScroll enabled={headerState.displayMobileMenu && !modalIsOpen}>
               <HeaderContainer inMobileSearch={headerState.displayMobileSearch}>
                  {breakpoints.isMobile() && <MobileSearch isOnCart={isOnCart} />}
                  <Section>
                     <MenuIcon
                        isActive={headerState.displayMobileMenu}
                        onClick={mobileMenuClicked}
                     />
                     <LogoComponent />
                  </Section>
                  <Section right>
                     {breakpoints.isTablet() && <SearchBar isOnCart={isOnCart} />}
                     <Dropdowns dropdowns={dropdowns} />
                  </Section>
                  <MobileMenu
                     links={links}
                     languageDropdown={dropdowns.languageDropdown}
                     stores={stores}
                  />
               </HeaderContainer>
            </RemoveScroll>
         </FocusLock>
      );
   }

   return (
      <HeaderWrapper>
         <HeaderContainer>
            <Section>
               <LogoComponent />
            </Section>
            <Headings links={links} stores={stores} />
            <Section right>
               <SearchBar isOnCart={isOnCart} />
               <Dropdowns dropdowns={dropdowns} />
            </Section>
         </HeaderContainer>
      </HeaderWrapper>
   );
};

export default connect(
   mapStateToProps,
   mapDispatchToProps
)(Header);
